import { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';
import { useTenant } from 'context/TenantContext';
import { exportPagePdf } from 'utils/exportPdf';

export default function RatiosPage() {
  // Identite du TENANT pour l'en-tete/pied des exports PDF.
  // Sans raison sociale, NIF et RCCM, l'export est REFUSE (cf. utils/exportPdf.js).
  const tenant = useTenant();
  const [data, setData] = useState({ ca_total: 0, total_charges: 0, marge_brute: 0, marge_nette: 0, taux_marge: 0, delai_client_moyen: 0, nb_factures_impayees: 0, montant_impaye: 0 });
  const printRef = useRef();
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await api.get('/comptabilite/ratios'); setData(r.data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--gss-text-light)' }}>Chargement...</p>;

  const ratios = [
    { label: "Chiffre d'affaires", value: `${Number(data.ca_total).toLocaleString()} FCFA`, color: '#2e7d32' },
    { label: 'Total charges', value: `${Number(data.total_charges).toLocaleString()} FCFA`, color: '#DC2626' },
    { label: 'Marge brute', value: `${Number(data.marge_brute).toLocaleString()} FCFA`, color: data.marge_brute >= 0 ? '#2e7d32' : '#DC2626' },
    { label: 'Marge nette', value: `${Number(data.marge_nette).toLocaleString()} FCFA`, color: data.marge_nette >= 0 ? '#2e7d32' : '#DC2626' },
    { label: 'Taux de marge', value: `${Number(data.taux_marge).toFixed(1)} %`, color: data.taux_marge >= 20 ? '#2e7d32' : '#f57c00' },
    { label: 'Delai client moyen', value: `${Number(data.delai_client_moyen).toFixed(0)} jours`, color: data.delai_client_moyen <= 30 ? '#2e7d32' : '#DC2626' },
    { label: 'Factures impayees', value: data.nb_factures_impayees, color: data.nb_factures_impayees > 0 ? '#DC2626' : '#2e7d32' },
    { label: 'Montant impaye', value: `${Number(data.montant_impaye).toLocaleString()} FCFA`, color: data.montant_impaye > 0 ? '#DC2626' : '#2e7d32' },
  ];

  return (
    <div>
      <div className="gss-toolbar" style={{ marginBottom: 16 }}>
        <div />
        <button className="gss-btn gss-btn-primary" onClick={() => exportPagePdf('Ratios financiers', printRef.current, tenant)}>Exporter PDF</button>
      </div>
      <div ref={printRef}>
      <div className="stats-grid">
        {ratios.slice(0, 4).map(r => (
          <div key={r.label} className="stat-card">
            <div className="stat-card-header"><span className="stat-card-label">{r.label}</span></div>
            <div className="stat-card-value" style={{ color: r.color }}>{r.value}</div>
          </div>
        ))}
      </div>

      <div className="content-card" style={{ marginTop: 16 }}>
        <div className="content-card-title">Ratios financiers</div>
        <div style={{ marginTop: 16 }}>
          {ratios.map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
              <span style={{ fontSize: 14 }}>{r.label}</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: r.color }}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="content-card" style={{ marginTop: 16, padding: '1rem' }}>
        <div className="content-card-title">Indicateurs de sante</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
          <div style={{ textAlign: 'center', padding: 16, borderRadius: 8, background: data.taux_marge >= 20 ? '#2e7d3210' : '#DC262610' }}>
            <div style={{ fontSize: 24 }}>{data.taux_marge >= 20 ? '●' : '●'}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: data.taux_marge >= 20 ? '#2e7d32' : '#DC2626', marginTop: 4 }}>Rentabilite</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{data.taux_marge >= 20 ? 'Bonne' : 'A surveiller'}</div>
          </div>
          <div style={{ textAlign: 'center', padding: 16, borderRadius: 8, background: data.delai_client_moyen <= 30 ? '#2e7d3210' : '#f57c0010' }}>
            <div style={{ fontSize: 24 }}>{data.delai_client_moyen <= 30 ? '●' : '●'}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: data.delai_client_moyen <= 30 ? '#2e7d32' : '#f57c00', marginTop: 4 }}>Tresorerie</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{data.delai_client_moyen <= 30 ? 'Saine' : 'Delais eleves'}</div>
          </div>
          <div style={{ textAlign: 'center', padding: 16, borderRadius: 8, background: data.nb_factures_impayees <= 3 ? '#2e7d3210' : '#DC262610' }}>
            <div style={{ fontSize: 24 }}>{data.nb_factures_impayees <= 3 ? '●' : '●'}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: data.nb_factures_impayees <= 3 ? '#2e7d32' : '#DC2626', marginTop: 4 }}>Recouvrement</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{data.nb_factures_impayees <= 3 ? 'Bon' : `${data.nb_factures_impayees} impayees`}</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
